import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { motion } from 'framer-motion';

const PreferencesPage = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  
  const [preferences, setPreferences] = useState({
    receivePromotions: true,
    receiveNewsletter: true,
    receiveOrderUpdates: true,
    darkMode: false,
    language: 'es'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const handleToggle = (preference: keyof typeof preferences) => {
    setPreferences({
      ...preferences,
      [preference]: !preferences[preference]
    });
    
    // Limpiar mensaje de éxito
    if (successMessage) {
      setSuccessMessage('');
    }
  };
  
  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPreferences({
      ...preferences,
      language: e.target.value
    });
    
    // Limpiar mensaje de éxito
    if (successMessage) {
      setSuccessMessage('');
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    try {
      // Simulación de guardado de preferencias
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mostrar mensaje de éxito
      setSuccessMessage('Tus preferencias han sido actualizadas correctamente');
    } catch (error) {
      console.error('Error al guardar preferencias:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!auth.isAuthenticated) {
    navigate('/login');
    return null;
  }
  
  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link to="/account" className="text-sm font-medium text-black hover:text-gray-700">
            ← Volver a mi cuenta
          </Link>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-xl shadow-sm overflow-hidden"
        >
          <div className="px-6 py-5 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">Preferencias</h1>
            <p className="mt-1 text-sm text-gray-500">Personaliza tu experiencia en Jarad.</p>
          </div>
          
          <div className="px-6 py-6">
            {successMessage && (
              <div className="mb-6 rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">{successMessage}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Idioma */}
              <div>
                <h2 className="text-base font-medium text-gray-900 mb-4">Idioma y región</h2>
                <div className="flex flex-col space-y-3">
                  <div>
                    <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                      Idioma
                    </label>
                    <select
                      id="language"
                      name="language"
                      value={preferences.language}
                      onChange={handleLanguageChange}
                      className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm"
                    >
                      <option value="es">Español</option>
                      <option value="en">English</option>
                      <option value="fr">Français</option>
                      <option value="de">Deutsch</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Notificaciones */}
              <div>
                <h2 className="text-base font-medium text-gray-900 mb-4">Notificaciones</h2>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="receivePromotions"
                        name="receivePromotions"
                        type="checkbox"
                        checked={preferences.receivePromotions}
                        onChange={() => handleToggle('receivePromotions')}
                        className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="receivePromotions" className="font-medium text-gray-700">Promociones y ofertas</label>
                      <p className="text-gray-500">Recibir notificaciones sobre descuentos y promociones especiales.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="receiveNewsletter"
                        name="receiveNewsletter"
                        type="checkbox"
                        checked={preferences.receiveNewsletter}
                        onChange={() => handleToggle('receiveNewsletter')}
                        className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="receiveNewsletter" className="font-medium text-gray-700">Boletín informativo</label>
                      <p className="text-gray-500">Recibir novedades, consejos y tendencias del sector.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="receiveOrderUpdates"
                        name="receiveOrderUpdates"
                        type="checkbox"
                        checked={preferences.receiveOrderUpdates}
                        onChange={() => handleToggle('receiveOrderUpdates')}
                        className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="receiveOrderUpdates" className="font-medium text-gray-700">Actualizaciones de pedidos</label>
                      <p className="text-gray-500">Recibir actualizaciones sobre el estado de tus pedidos.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Apariencia */}
              <div>
                <h2 className="text-base font-medium text-gray-900 mb-4">Apariencia</h2>
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="darkMode"
                      name="darkMode"
                      type="checkbox"
                      checked={preferences.darkMode}
                      onChange={() => handleToggle('darkMode')}
                      className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="darkMode" className="font-medium text-gray-700">Modo oscuro</label>
                    <p className="text-gray-500">Usar tema oscuro para la interfaz de usuario.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-end">
                <Link
                  to="/account"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black mr-3"
                >
                  Cancelar
                </Link>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Guardando...
                    </>
                  ) : 'Guardar preferencias'}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PreferencesPage; 